import { Anchor, List, Stack, Text, Title } from "@mantine/core";
import { IconChevronRight } from "@tabler/icons-react";
import type { PropsWithChildren } from "react";
import { ContentShell } from "../../layout/ContentShell";

export default function TermsOfUse() {
  return (
    <ContentShell title="Nutzungsbedingungen für das Dokumentenportal" pageTitle="Nutzungsbedingungen" center>
      <Stack w="50%">
        <Stack>
          <Title order={3}>1. Geltungsbereich</Title>
          <Text>
            Diese Nutzungsbedingungen regeln den Zugang und die Verwendung des Dokumentenportals («Portal»), welches die wichtigsten
            Dokumente, während dem Basislehrjahr für die jeweiligen Lernenden elektronisch bereitstellt. Die Dokumente können mit einer
            qualifizierten elektronischen Signatur versehen werden. Mit der Nutzung des Portals anerkennen die Nutzerinnen und Nutzer
            («Nutzende») diese Bedingungen als verbindlich.
          </Text>
        </Stack>

        <Stack>
          <Title order={3}>2. Zweck des Portals</Title>
          <Text>
            Das Portal dient der sicheren Bereitstellung von Dokumenten, die mit einer elektronischen Signatur versehen werden. Diese
            Signaturen erfüllen die gesetzlichen Anforderungen und sind rechtsgültig.
          </Text>
        </Stack>

        <Stack>
          <Title order={3}>3. Zugang und Authentifizierung</Title>
          <TermsOfUseList>
            <List.Item>Der Zugang erfolgt ausschliesslich für berechtigte Personen über eine sichere Authentifizierung.</List.Item>
            <List.Item>
              Die Nutzenden sind verpflichtet, ihre Zugangsdaten vertraulich zu behandeln und vor unbefugtem Zugriff zu schützen.
            </List.Item>
            <List.Item>Jeglicher Missbrauch oder Verdacht auf Missbrauch ist umgehend dem Betreiber des Portals zu melden. </List.Item>
          </TermsOfUseList>
        </Stack>

        <Stack>
          <Title order={3}>4. Elektronische Signatur</Title>
          <TermsOfUseList>
            <List.Item>
              Dokumente, die im Portal zur Verfügung gestellt werden, können mit einer qualifizierten elektronischen Signatur versehen sein.
            </List.Item>
            <List.Item>Die qualifizierte elektronische Signatur ist der handschriftlichen Unterschrift rechtlich gleichgestellt.</List.Item>
            <List.Item>
              Mit der Nutzung des Portals erkennen die Nutzenden die Rechtsverbindlichkeit der elektronisch signierten Dokumente an.
            </List.Item>
          </TermsOfUseList>
        </Stack>

        <Stack>
          <Title order={3}>5. Verantwortlichkeiten der Nutzenden</Title>
          <TermsOfUseList>
            <List.Item>
              Die Nutzenden sind verpflichtet, die Dokumente sorgfältig zu prüfen, bevor sie eine elektronische Signatur anbringen oder
              akzeptieren.
            </List.Item>
            <List.Item>
              Die Nutzenden tragen die Verantwortung für alle Handlungen, die mit ihren Zugangsdaten vorgenommen werden.
            </List.Item>
            <List.Item>Eine Weitergabe der Zugangsdaten und der Inhalte an Dritte ist untersagt.</List.Item>
          </TermsOfUseList>
        </Stack>

        <Stack>
          <Title order={3}>6. Verantwortlichkeiten des Betreibers</Title>
          <TermsOfUseList>
            <List.Item>
              Der Betreiber stellt sicher, dass die technischen und organisatorischen Massnahmen den geltenden Sicherheitsstandards
              entsprechen.
            </List.Item>
            <List.Item>
              Der Betreiber übernimmt keine Verantwortung für Rechtsfolgen, die sich aus einer falschen oder unrechtmässigen Nutzung durch
              die Nutzenden ergeben.
            </List.Item>
            <List.Item>Eine Haftung für technische Ausfälle oder zeitweise Nichtverfügbarkeit des Portals ist ausgeschlossen.</List.Item>
          </TermsOfUseList>
        </Stack>

        <Stack>
          <Title order={3}>7. Datenschutz</Title>
          <TermsOfUseList>
            <List.Item>Die Bearbeitung personenbezogener Daten erfolgt gemäss den geltenden Gesetzen.</List.Item>
            <List.Item>
              Personenbezogene Daten werden ausschliesslich für die Nutzung des Portals und zur Erfüllung der rechtlichen Anforderungen
              verarbeitet.
            </List.Item>
            <List.Item>
              Weitere Informationen sind der Datenschutzerklärung auf der Webseite des ZLI (
              <Anchor href="https://zli.ch" target="_blank">
                https://zli.ch
              </Anchor>
              ) zu entnehmen.
            </List.Item>
          </TermsOfUseList>
        </Stack>

        <Stack>
          <Title order={3}>8. Verfügbarkeit und Änderungen</Title>
          <TermsOfUseList>
            <List.Item>
              Der Betreiber ist bestrebt, das Portal jederzeit verfügbar zu halten, kann jedoch die ununterbrochene Verfügbarkeit nicht
              garantieren.
            </List.Item>
            <List.Item>
              Der Betreiber behält sich das Recht vor, diese Nutzungsbedingungen jederzeit zu ändern. Die jeweils aktuelle Fassung wird im
              Portal veröffentlicht.
            </List.Item>
          </TermsOfUseList>
        </Stack>

        <Stack>
          <Title order={3}>9. Anwendbares Recht und Gerichtsstand</Title>
          <TermsOfUseList>
            <List.Item>Auf diese Nutzungsbedingungen findet ausschliesslich materielles Recht der Schweiz Anwendung.</List.Item>
            <List.Item>
              Gerichtsstand ist der Sitz des Betreibers, sofern nicht zwingendes Recht einen anderen Gerichtsstand vorsieht.
            </List.Item>
          </TermsOfUseList>
        </Stack>
      </Stack>
    </ContentShell>
  );
}

function TermsOfUseList({ children }: PropsWithChildren) {
  return (
    <List
      icon={
        <Stack justify="center">
          <IconChevronRight size={15} />
        </Stack>
      }
      spacing="lg"
    >
      {children}
    </List>
  );
}
